import { toast } from 'sonner';
import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { SupplierWithEmails } from '@durma-soft/gros-sdk/dist/types/suppliers';
import {
  useAuthUserRestaurant,
  useDeleteSupplierMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';

import { Button } from '@/components/shared/shadcn-ui/button';
import { ConfirmationModal } from '@/components/shared/confirmation-modal';
import { EditSupplierModal } from '@/components/restaurant/suppliers/edit-supplier-modal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/shared/shadcn-ui/tooltip';

import { TType } from '@/types/general';

const SupplierActionsCell = ({
  supplier,
}: {
  supplier: SupplierWithEmails;
}) => {
  const { t } = useTranslation();
  const restaurant = useAuthUserRestaurant();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { isPending, mutateAsync } = useDeleteSupplierMutation();

  const deleteSupplier = async () => {
    try {
      await mutateAsync({
        restaurant_id: restaurant.id,
        supplier_id: supplier.id,
      });
      toast.success(t('suppliers.delete-success-msg'));
    } catch (error) {
      // 422 = supplier still referenced by products
      if (error instanceof ValidationError) {
        toast.error(t('suppliers.delete-in-use-msg'));
      } else {
        toast.error(t('suppliers.delete-error-msg'));
      }
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex items-center justify-center gap-5">
        {/* Edit */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center justify-center text-gray-700 p-0"
              onClick={() => setIsEditOpen(true)}
            >
              <Pencil className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('suppliers.edit-details')}</TooltipContent>
        </Tooltip>
        {/* Delete */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center justify-center text-gray-700 p-0"
              onClick={() => setIsConfirmOpen(true)}
            >
              <Trash2 className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('common.delete')}</TooltipContent>
        </Tooltip>
      </div>
      {isEditOpen && (
        <EditSupplierModal
          supplier={supplier}
          onClose={() => setIsEditOpen(false)}
        />
      )}
      <ConfirmationModal
        isLoading={isPending}
        isModalOpen={isConfirmOpen}
        confirmationText={t('common.delete')}
        title={t('suppliers.delete')}
        description={t('suppliers.delete-details')}
        onConfirm={deleteSupplier}
        onCancel={() => setIsConfirmOpen(false)}
        closeModal={() => setIsConfirmOpen(false)}
      />
    </TooltipProvider>
  );
};

export const supplierColumns = (t: TType): ColumnDef<SupplierWithEmails>[] => [
  {
    accessorKey: 'name',
    header: t('common.name'),
  },
  {
    id: 'emails',
    enableSorting: false,
    header: t('suppliers.emails'),
    cell: (cellData) =>
      cellData.row.original.emails.map((email) => email.email).join(', '),
  },
  {
    id: 'actions',
    enableSorting: false,
    header: '',
    cell: (cellData) => (
      <SupplierActionsCell supplier={cellData.row.original} />
    ),
  },
];
